import React, { useRef, useState } from "react";
import Card from "components/card";
import { MdStraighten, MdExpandMore, MdExpandLess } from "react-icons/md";
import { useUserMeasurementHistory } from "../../hooks/useMeasurements";
import { useImageLightbox } from "../../hooks/useImageLightbox";
import {
  MeasurementType,
  MEASUREMENT_TYPE_LABELS,
  MEASUREMENT_TYPE_OPTIONS,
} from "../../types/measurement";
import {
  DatedMeasurementGroup,
  MeasurementDisplayRow,
  buildDisplayRows,
} from "../../utils/measurementGrouping";
import FilterDropdown from "../common/FilterDropdown";
import DateFilterPicker from "../common/DateFilterPicker";
import ActiveFilterBadge from "../common/ActiveFilterBadge";
import BlurredImage from "../common/BlurredImage";
import BlurToggleButton from "../common/BlurToggleButton";
import CardHeader from "../common/CardHeader";
import EmptyState from "../common/EmptyState";
import ImageLightbox, { buildFileSlides } from "../common/ImageLightbox";
import ErrorMessage from "../common/ErrorMessage";
import LoadingSpinner from "../common/LoadingSpinner";
import Pagination from "../common/Pagination";
import { formatDateLong } from "../../utils/dateUtils";
import { formatLabels, signTone } from "../../utils/formatters";

interface UserMeasurementLogsCardProps {
  userId: number | null;
}

// The mobile card shows three rows before "show more"
const COLLAPSED_ROWS = 3;
const MAX_THUMBNAILS = 3;

const typeLabel = (type: MeasurementType): string =>
  MEASUREMENT_TYPE_LABELS[type] ?? formatLabels(type);

const UserMeasurementLogsCard: React.FC<UserMeasurementLogsCardProps> = ({
  userId,
}) => {
  const {
    groups,
    previousOf,
    loading,
    error,
    filters,
    page,
    limit,
    totalDates,
    totalPages,
    totalMeasurements,
    updateFilters,
    changePage,
  } = useUserMeasurementHistory(userId);

  // Paging scrolls here, not to the top of the page
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isBlurred, setIsBlurred] = useState(true);
  const [expandedDates, setExpandedDates] = useState<string[]>([]);
  const { openAt, lightboxProps } = useImageLightbox();

  const hasFilters = !!filters.measurement_type || !!filters.measured_at;

  const rowsByDate = groups.map((group) => ({
    group,
    rows: buildDisplayRows(group, previousOf(group)),
  }));

  const hasPhotos = rowsByDate.some(({ rows }) =>
    rows.some((row) => row.attachments.length > 0)
  );

  const toggleDate = (date: string) =>
    setExpandedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );

  const renderThumbnails = (row: MeasurementDisplayRow, date: string) => {
    const visible = row.attachments.slice(0, MAX_THUMBNAILS);
    const overflow = row.attachments.length - visible.length;

    return (
      <div className="flex flex-shrink-0 items-center gap-2 md:ml-auto">
        {visible.map((attachment, index) => (
          <BlurredImage
            key={attachment.id}
            src={attachment.full_path as string}
            alt={attachment.original_name}
            blurred={isBlurred}
            blurClass="blur-[6px]"
            onClick={() =>
              openAt(buildFileSlides(row.attachments, row.label, date), index)
            }
            className="h-12 w-12 cursor-pointer rounded hover:scale-105"
          />
        ))}
        {overflow > 0 && (
          <button
            onClick={() =>
              openAt(
                buildFileSlides(row.attachments, row.label, date),
                MAX_THUMBNAILS
              )
            }
            className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            +{overflow}
          </button>
        )}
      </div>
    );
  };

  const renderRow = (row: MeasurementDisplayRow, date: string) => (
    <div
      key={row.key}
      className="border-b border-gray-100 py-2 last:border-0 dark:border-gray-700"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-sm text-gray-600 dark:text-gray-300">
            {row.label}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {row.change && (
            <span className={`text-xs font-medium ${signTone(row.change)}`}>
              {row.change}
            </span>
          )}
          <span className="text-sm font-semibold text-navy-700 dark:text-white">
            {row.value}
          </span>
        </div>
      </div>
      {/* Stacked on mobile; on desktop notes sit left, images right */}
      {(row.notes || row.attachments.length > 0) && (
        <div className="mt-1 flex flex-col gap-2 md:flex-row md:items-start">
          {row.notes && (
            <p className="text-xs text-gray-500 dark:text-gray-400 md:flex-1">
              {row.notes}
            </p>
          )}
          {row.attachments.length > 0 && renderThumbnails(row, date)}
        </div>
      )}
    </div>
  );

  const renderDateCard = ({
    group,
    rows,
  }: {
    group: DatedMeasurementGroup;
    rows: MeasurementDisplayRow[];
  }) => {
    const isExpanded = expandedDates.includes(group.date);
    const visibleRows = isExpanded ? rows : rows.slice(0, COLLAPSED_ROWS);
    const hiddenCount = rows.length - visibleRows.length;

    return (
      <div
        key={group.date}
        className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
      >
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="font-semibold text-navy-700 dark:text-white">
            {formatDateLong(group.date)}
          </h3>
          <span className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
            {rows.length} measurement{rows.length !== 1 ? "s" : ""}
          </span>
        </div>

        {visibleRows.map((row) => renderRow(row, group.date))}

        {/* Only when there is genuinely more than the collapsed view shows -
            an expanded date whose FILTERED rows fit in view needs no toggle */}
        {rows.length > COLLAPSED_ROWS && (
          <button
            onClick={() => toggleDate(group.date)}
            className="mt-2 flex items-center gap-1 text-sm font-medium text-biafit-primary transition-colors hover:text-biafit-secondary"
          >
            {isExpanded ? (
              <>
                <MdExpandLess className="h-4 w-4" />
                Show less
              </>
            ) : (
              <>
                <MdExpandMore className="h-4 w-4" />
                Show {hiddenCount} more
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorMessage message={error} />;

    if (groups.length === 0) {
      return (
        <EmptyState
          icon={MdStraighten}
          title={
            hasFilters ? "No measurements match" : "No measurements recorded"
          }
          description={
            hasFilters
              ? "Try a different measurement type or date."
              : "This user has not logged any body measurements yet."
          }
        />
      );
    }

    return <div className="space-y-4">{rowsByDate.map(renderDateCard)}</div>;
  };

  return (
    <Card extra="w-full p-3 md:p-6">
      <CardHeader
        title="Body Measurements"
        count={loading ? undefined : totalMeasurements}
        singular="Measurement"
        scrollRef={sectionRef}
        actions={
          hasPhotos && (
            <BlurToggleButton
              blurred={isBlurred}
              onToggle={() => setIsBlurred(!isBlurred)}
            />
          )
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FilterDropdown
          label="Measurement Type"
          options={MEASUREMENT_TYPE_OPTIONS}
          value={filters.measurement_type}
          onChange={(value) =>
            updateFilters({
              ...filters,
              measurement_type: (value as MeasurementType) || undefined,
            })
          }
          placeholder="All Types"
        />
        <DateFilterPicker
          label="Date"
          value={filters.measured_at}
          onChange={(value) =>
            updateFilters({ ...filters, measured_at: value || undefined })
          }
          placeholder="Any date"
        />
      </div>

      {/* FilterDropdown shows only its label; DateFilterPicker shows its value */}
      {filters.measurement_type && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <ActiveFilterBadge
            label={typeLabel(filters.measurement_type)}
            colorScheme="purple"
            onClear={() =>
              updateFilters({ ...filters, measurement_type: undefined })
            }
          />
        </div>
      )}

      {renderBody()}

      <Pagination
        page={page}
        limit={limit}
        total={totalDates}
        totalPages={totalPages}
        entityName="dates"
        onPageChange={changePage}
        loading={loading}
        scrollTargetRef={sectionRef}
      />

      <ImageLightbox {...lightboxProps} />
    </Card>
  );
};

export default UserMeasurementLogsCard;
